import { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Mail, Lock, User, Eye, EyeOff, ArrowRight } from 'lucide-react';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

export default function LoginPage() {
  const [tab, setTab] = useState('signin'); // 'signin' | 'signup'
  const [form, setForm] = useState({ name: '', email: '', password: '', confirm: '' });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/';
  const wishlistProductId = location.state?.wishlistProductId || null;

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSuccess = async (token, user) => {
    login(token, user);
    if (wishlistProductId) {
      try {
        await api.post(`/wishlist/${wishlistProductId}`);
        toast.success('Added to wishlist!', { icon: '❤️', duration: 1500 });
      } catch {}
    }
    navigate(from, { replace: true });
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password) return toast.error('Enter email and password');
    setLoading(true);
    try {
      const { data } = await api.post('/auth/login', { email: form.email, password: form.password });
      toast.success('Welcome back!');
      handleSuccess(data.token, data.user);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password) return toast.error('All fields are required');
    if (form.password.length < 6) return toast.error('Password must be at least 6 characters');
    if (form.password !== form.confirm) return toast.error('Passwords do not match');
    setLoading(true);
    try {
      const { data } = await api.post('/auth/register', { name: form.name, email: form.email, password: form.password });
      toast.success('Account created!');
      handleSuccess(data.token, data.user);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  // Forgot password state
  const [forgotStep, setForgotStep] = useState(null); // null | 'email' | 'otp' | 'reset'
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotOtp, setForgotOtp] = useState('');
  const [forgotNewPass, setForgotNewPass] = useState('');
  const [forgotConfirm, setForgotConfirm] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);

  const resetForgotState = () => {
    setForgotStep(null);
    setForgotEmail(''); setForgotOtp(''); setForgotNewPass(''); setForgotConfirm('');
  };

  const handleForgotSendOtp = async (e) => {
    e.preventDefault();
    if (!forgotEmail) return toast.error('Enter your email');
    setForgotLoading(true);
    try {
      await api.post('/auth/forgot-password', { email: forgotEmail });
      toast.success('OTP sent to your email');
      setForgotOtp('');
      setForgotStep('otp');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send OTP');
    } finally { setForgotLoading(false); }
  };

  // Step 1: validate the OTP before showing the password fields
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (forgotOtp.length !== 6) return toast.error('Enter the 6-digit OTP');
    setForgotLoading(true);
    try {
      await api.post('/auth/verify-reset-otp', { email: forgotEmail, otp: forgotOtp });
      toast.success('OTP verified');
      setForgotStep('reset');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Invalid or expired OTP');
    } finally { setForgotLoading(false); }
  };

  // Step 2: set the new password (with confirm-match check)
  const handleForgotReset = async (e) => {
    e.preventDefault();
    if (!forgotNewPass || !forgotConfirm) return toast.error('Enter and confirm your new password');
    if (forgotNewPass.length < 6) return toast.error('Password must be at least 6 characters');
    if (forgotNewPass !== forgotConfirm) return toast.error('Passwords do not match');
    setForgotLoading(true);
    try {
      await api.post('/auth/reset-password', { email: forgotEmail, otp: forgotOtp, new_password: forgotNewPass, confirm_password: forgotConfirm });
      toast.success('Password reset! Please sign in with your new password.');
      resetForgotState();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Invalid or expired OTP');
    } finally { setForgotLoading(false); }
  };

  const handleGoogle = () => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    const redirectUri = `${window.location.origin}/auth/google/callback`;
    const scope = encodeURIComponent('email profile');
    const url = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=token&scope=${scope}`;
    window.location.href = url;
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2">
            <div className="w-10 h-10 bg-brand-yellow rounded-xl flex items-center justify-center font-black text-brand-dark text-lg">Y</div>
            <span className="text-xl font-bold text-brand-dark">TheYellove</span>
          </Link>
          <p className="text-gray-500 text-sm mt-2">Made with Love, For the Ones You Love ❤️</p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          {/* Tabs */}
          <div className="flex border-b border-gray-100">
            {['signin', 'signup'].map((t) => (
              <button key={t} onClick={() => setTab(t)}
                className={`flex-1 py-4 text-sm font-semibold transition-colors ${tab === t ? 'text-brand-dark border-b-2 border-brand-yellow' : 'text-gray-400 hover:text-gray-600'}`}>
                {t === 'signin' ? 'Sign In' : 'Sign Up'}
              </button>
            ))}
          </div>

          <div className="p-6 space-y-4">
            {/* Google Button */}
            <button onClick={handleGoogle}
              className="w-full flex items-center justify-center gap-3 border border-gray-200 rounded-xl py-3 px-4 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </button>

            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-gray-100" />
              <span className="text-xs text-gray-400">or</span>
              <div className="flex-1 h-px bg-gray-100" />
            </div>

            {/* Sign In Form */}
            {tab === 'signin' && (
              <form onSubmit={handleSignIn} className="space-y-3">
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input type="email" value={form.email} onChange={set('email')} required
                    className="w-full border border-gray-200 rounded-xl pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-yellow focus:border-transparent"
                    placeholder="Email address" />
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input type={showPass ? 'text' : 'password'} value={form.password} onChange={set('password')} required
                    className="w-full border border-gray-200 rounded-xl pl-10 pr-10 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-yellow focus:border-transparent"
                    placeholder="Password" />
                  <button type="button" onClick={() => setShowPass((s) => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <div className="text-right">
                  <button type="button" onClick={() => setForgotStep('email')}
                    className="text-xs text-brand-yellow hover:underline font-medium">
                    Forgot Password?
                  </button>
                </div>
                <button type="submit" disabled={loading}
                  className="w-full bg-brand-yellow hover:bg-brand-yellow-dark text-brand-dark font-bold py-3 rounded-xl flex items-center justify-center gap-2 transition-colors disabled:opacity-50">
                  {loading ? <div className="w-5 h-5 border-2 border-brand-dark/30 border-t-brand-dark rounded-full animate-spin" /> : <>Sign In <ArrowRight className="w-4 h-4" /></>}
                </button>
              </form>
            )}

            {/* Forgot Password Modal */}
            {forgotStep && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
                <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-2xl">
                  <h3 className="text-lg font-bold text-brand-dark mb-1">
                    {forgotStep === 'email' ? 'Forgot Password?' : forgotStep === 'otp' ? 'Enter OTP' : 'Set New Password'}
                  </h3>
                  <p className="text-xs text-gray-500 mb-4">
                    {forgotStep === 'email'
                      ? 'Enter your email — we\'ll send an OTP.'
                      : forgotStep === 'otp'
                        ? `OTP sent to ${forgotEmail}`
                        : 'Create a new password for your account.'}
                  </p>

                  {forgotStep === 'email' && (
                    <form onSubmit={handleForgotSendOtp} className="space-y-3">
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input type="email" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} required
                          className="w-full border border-gray-200 rounded-xl pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-yellow"
                          placeholder="Email address" />
                      </div>
                      <button type="submit" disabled={forgotLoading}
                        className="w-full bg-brand-yellow text-brand-dark font-bold py-3 rounded-xl disabled:opacity-50">
                        {forgotLoading ? 'Sending...' : 'Send OTP'}
                      </button>
                    </form>
                  )}

                  {forgotStep === 'otp' && (
                    <form onSubmit={handleVerifyOtp} className="space-y-3">
                      <input type="text" value={forgotOtp} onChange={e => setForgotOtp(e.target.value.replace(/\D/g, ''))} required
                        autoComplete="one-time-code" inputMode="numeric" name="reset-otp" autoFocus
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-center tracking-widest font-bold focus:outline-none focus:ring-2 focus:ring-brand-yellow"
                        placeholder="Enter 6-digit OTP" maxLength={6} />
                      <button type="submit" disabled={forgotLoading || forgotOtp.length !== 6}
                        className="w-full bg-brand-yellow text-brand-dark font-bold py-3 rounded-xl disabled:opacity-50">
                        {forgotLoading ? 'Verifying...' : 'Verify OTP'}
                      </button>
                      <button type="button" onClick={handleForgotSendOtp} disabled={forgotLoading}
                        className="w-full text-xs text-gray-400 hover:text-gray-600 disabled:opacity-50">
                        Resend OTP
                      </button>
                    </form>
                  )}

                  {forgotStep === 'reset' && (
                    <form onSubmit={handleForgotReset} className="space-y-3">
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input type={showPass ? 'text' : 'password'} value={forgotNewPass} onChange={e => setForgotNewPass(e.target.value)} required
                          autoComplete="new-password"
                          className="w-full border border-gray-200 rounded-xl pl-10 pr-10 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-yellow"
                          placeholder="New password (min 6 chars)" />
                        <button type="button" onClick={() => setShowPass((s) => !s)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                          {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input type={showPass ? 'text' : 'password'} value={forgotConfirm} onChange={e => setForgotConfirm(e.target.value)} required
                          autoComplete="new-password"
                          className="w-full border border-gray-200 rounded-xl pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-yellow"
                          placeholder="Confirm new password" />
                      </div>
                      {forgotConfirm && forgotNewPass !== forgotConfirm && (
                        <p className="text-xs text-red-500">Passwords do not match</p>
                      )}
                      <button type="submit" disabled={forgotLoading || !forgotNewPass || forgotNewPass !== forgotConfirm}
                        className="w-full bg-brand-yellow text-brand-dark font-bold py-3 rounded-xl disabled:opacity-50">
                        {forgotLoading ? 'Resetting...' : 'Reset Password'}
                      </button>
                    </form>
                  )}

                  <button onClick={resetForgotState}
                    className="mt-3 w-full text-xs text-gray-400 hover:text-gray-600">
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Sign Up Form */}
            {tab === 'signup' && (
              <form onSubmit={handleSignUp} className="space-y-3">
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input type="text" value={form.name} onChange={set('name')} required
                    className="w-full border border-gray-200 rounded-xl pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-yellow focus:border-transparent"
                    placeholder="Your name" />
                </div>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input type="email" value={form.email} onChange={set('email')} required
                    className="w-full border border-gray-200 rounded-xl pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-yellow focus:border-transparent"
                    placeholder="Email address" />
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input type={showPass ? 'text' : 'password'} value={form.password} onChange={set('password')} required
                    className="w-full border border-gray-200 rounded-xl pl-10 pr-10 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-yellow focus:border-transparent"
                    placeholder="Password (min 6 characters)" />
                  <button type="button" onClick={() => setShowPass((s) => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input type="password" value={form.confirm} onChange={set('confirm')} required
                    className="w-full border border-gray-200 rounded-xl pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-yellow focus:border-transparent"
                    placeholder="Confirm password" />
                </div>
                <button type="submit" disabled={loading}
                  className="w-full bg-brand-yellow hover:bg-brand-yellow-dark text-brand-dark font-bold py-3 rounded-xl flex items-center justify-center gap-2 transition-colors disabled:opacity-50">
                  {loading ? <div className="w-5 h-5 border-2 border-brand-dark/30 border-t-brand-dark rounded-full animate-spin" /> : <>Create Account <ArrowRight className="w-4 h-4" /></>}
                </button>
              </form>
            )}

            <p className="text-center text-xs text-gray-400">
              By continuing, you agree to our{' '}
              <Link to="/privacy-policy" className="text-brand-dark underline">Privacy Policy</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
